import React, { useEffect, useState, useRef, useCallback } from "react";
import { createChatBotMessage } from "react-chatbot-kit";
import markdownTest from './markdowntest';

/**
 * ActionProvider with EarSketch editor integration.
 *
 * Features:
 * 1. Handshake with EarSketch parent via postMessage (parent sends "init").
 * 2. Request current editor contents from parent using {fn:"getEditorContents"}.
 * 3. Store latest code locally (editorCode state).
 * 4. Include latest code in payload to backend /api/chat.
 * 5. Preserve function-call pathway (addNumbers / subtractNumbers).
 * 6. Provide a Refresh Code action for UI widgets.
 *
 * SECURITY NOTE: parentOriginRef is set on first trusted message. Update `ALLOWED_PARENT_ORIGINS`
 * if you want strict allow-list enforcement.
 */

// Backend base URL
const CHATBOT_API_BASE =
  process.env.REACT_APP_CHATBOT_API_BASE || "http://localhost:8080";

// Set to a non-empty array to restrict which origins can send messages (e.g. prod enforcement).
// Empty = accept messages from any origin, matching the EarSketch extension example.
const ALLOWED_PARENT_ORIGINS = [];
const MULTILINE_TOKEN = "__CHATBOT_NL__";

// Helper to safely parse JSON
const safeJSONParse = (str) => {
  try {
    return JSON.parse(str);
  } catch {
    return str;
  }
};

const encodeMultiline = (text) =>
  typeof text === "string" ? text.replace(/\n/g, MULTILINE_TOKEN) : text;

const decodeMultiline = (text) =>
  typeof text === "string" ? text.split(MULTILINE_TOKEN).join("\n") : text;


const ActionProvider = ({ setState, children }) => {
  const [editorCode, setEditorCode] = useState("");
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [userId, setUserId] = useState(null);
  const parentOriginRef = useRef(null);
  // FIFO queue of pending resolvers — EarSketch responds in the order requests were sent,
  // and does not echo back any id, so we match responses by arrival order.
  const pendingQueue = useRef([]); // Array of { resolve, fnName }
  const sessionInitialized = useRef(false);
  const isInEarSketch = useRef(false);
  
  // Check if we're embedded in EarSketch
  useEffect(() => {
    isInEarSketch.current = window.self !== window.parent;
    console.log("[CHATBOT] Running in iframe:", isInEarSketch.current);
    console.log("[CHATBOT] Parent origin:", window.location.ancestorOrigins?.[0] || "unknown");
  }, []);

  // ----------------- Prefill the chatbot input (DOM-based) -----------------
  // react-chatbot-kit does not expose an input setter, so we set the DOM value
  // and dispatch an input event so the library/React updates its internal state.
  const prefillChatInput = useCallback((text) => {
    const el = document.querySelector(
      ".react-chatbot-kit-chat-input, .react-chatbot-kit-chat-input textarea"
    );

    if (!el) {
      console.warn(
        "[CHATBOT] Could not find .react-chatbot-kit-chat-input to prefill."
      );
      return;
    }

    const proto =
      el.tagName === "TEXTAREA"
        ? window.HTMLTextAreaElement.prototype
        : window.HTMLInputElement.prototype;
    const valueSetter = Object.getOwnPropertyDescriptor(proto, "value")?.set;

    if (valueSetter) valueSetter.call(el, text);
    else el.value = text;

    el.dispatchEvent(new Event("input", { bubbles: true }));
  }, []);

  // Enable multiline composition on top of react-chatbot-kit's single-line input.
  // Shift+Enter inserts a line break, Enter sends.
  useEffect(() => {
    const setupMultilineInput = () => {
      const form = document.querySelector(".react-chatbot-kit-chat-input-form");
      if (!form) return false;

      const hiddenInput = form.querySelector(
        "input.react-chatbot-kit-chat-input"
      );
      if (!hiddenInput) return false;

      if (form.querySelector("textarea.chat-multiline-input")) return true;

      const textarea = document.createElement("textarea");
      textarea.className = "react-chatbot-kit-chat-input chat-multiline-input";
      textarea.rows = 2;
      textarea.placeholder = hiddenInput.getAttribute("placeholder") || "";
      textarea.value = decodeMultiline(hiddenInput.value || "");

      hiddenInput.style.display = "none";
      hiddenInput.setAttribute("aria-hidden", "true");
      hiddenInput.tabIndex = -1;

      const hiddenSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        "value"
      )?.set;

      const syncHiddenInput = (value) => {
        const encoded = encodeMultiline(value);
        if (hiddenSetter) hiddenSetter.call(hiddenInput, encoded);
        else hiddenInput.value = encoded;
        hiddenInput.dispatchEvent(new Event("input", { bubbles: true }));
      };

      textarea.addEventListener("input", () => {
        syncHiddenInput(textarea.value);
      });

      textarea.addEventListener("keydown", (event) => {
        if (event.key === "Enter" && event.shiftKey) {
          event.preventDefault();
          const start = textarea.selectionStart ?? textarea.value.length;
          const end = textarea.selectionEnd ?? textarea.value.length;
          textarea.value =
            textarea.value.slice(0, start) + "\n" + textarea.value.slice(end);
          const cursor = start + 1;
          textarea.selectionStart = cursor;
          textarea.selectionEnd = cursor;
          syncHiddenInput(textarea.value);
          return;
        }

        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          syncHiddenInput(textarea.value);
          form.requestSubmit();
        }
      });

      form.addEventListener("submit", () => {
        window.requestAnimationFrame(() => {
          textarea.value = decodeMultiline(hiddenInput.value || "");
        });
      });

      form.insertBefore(textarea, hiddenInput);
      return true;
    };

    setupMultilineInput();

    const observer = new MutationObserver(() => {
      setupMultilineInput();
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    console.log(`[CHATBOT] isDarkTheme changed →`, isDarkTheme);
  }, [isDarkTheme]);

  // ----------------- Generic function caller -----------------
  // Sends {fn: fnName} without an id field — EarSketch does not echo ids back.
  // Responses are matched FIFO via pendingQueue.
  const callEarSketchFunction = useCallback((fnName) => {
    return new Promise((resolve) => {
      if (!isInEarSketch.current) {
        console.warn(`[CHATBOT] Not in EarSketch iframe, cannot call ${fnName}`);
        resolve("");
        return;
      }

      const entry = { resolve, fnName };
      pendingQueue.current.push(entry);

      const origin = parentOriginRef.current || "*";
      console.log(`[CHATBOT] Calling parent function: ${fnName}`);
      window.parent.postMessage(JSON.stringify({ fn: fnName }), origin);

      // Safety timeout — remove from queue if EarSketch never responds
      setTimeout(() => {
        const idx = pendingQueue.current.indexOf(entry);
        if (idx !== -1) {
          console.warn(`[CHATBOT] ${fnName} never responded, cleaning up`);
          pendingQueue.current.splice(idx, 1);
          resolve("");
        }
      }, 5000);
    });
  }, []);

  // ----------------- Message listener from parent -----------------
  useEffect(() => {
    const handleMessage = (event) => {
      if (
        ALLOWED_PARENT_ORIGINS.length > 0 &&
        !ALLOWED_PARENT_ORIGINS.includes(event.origin)
      )
        return;

      if (!parentOriginRef.current) parentOriginRef.current = event.origin;

      if (event.data === "init") {
        // Fetch editor contents on handshake; color theme + user are handled on mount.
        callEarSketchFunction("getEditorContents").then((code) => {
          if (typeof code === "string" && code) setEditorCode(code);
        });
        return;
      }

      let data = safeJSONParse(event.data);

      // Resolve the oldest pending request (FIFO) — EarSketch responds in order
      // and does not echo back an id.
      if (pendingQueue.current.length > 0) {
        const { resolve, fnName } = pendingQueue.current.shift();
        console.log(`[CHATBOT] Resolving response for: ${fnName}`, typeof data, data);
        resolve(data);
        return;
      }

      // Legacy: handle code updates
      if (data?.type === "code_update" && typeof data.code === "string") {
        setEditorCode(data.code);
      }

      // Handle color theme change messages (optional future updates)
      if (data?.messageType === "colorThemeChanged") {
        const theme = data.colorTheme;
        setIsDarkTheme(theme === "dark");
        const root = document.documentElement;
        if (theme === "dark") {
          root.classList.add('dark');
          root.setAttribute('data-theme', 'dark');
        } else {
          root.classList.remove('dark');
          root.setAttribute('data-theme', 'light');
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [callEarSketchFunction]);

  // ----------------- Initialize session with backend -----------------
  const initializeSession = useCallback(async (uid) => {
    if (!uid || sessionInitialized.current) return;
    
    try {
      const res = await fetch(`${CHATBOT_API_BASE}/api/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: uid }),
      });
      
      if (res.ok) {
        const data = await res.json();
        console.log("[CHATBOT] Session initialized:", data);
        sessionInitialized.current = true;
      } else {
        console.warn("[CHATBOT] Failed to initialize session:", await res.text());
      }
    } catch (err) {
      console.error("[CHATBOT] Error initializing session:", err);
    }
  }, []);

  // ----------------- Handle user messages -----------------
  const handleUserMessage = async (message) => {
    const decodedMessage = decodeMultiline(message);

    // Show loading message
    const botLoading = createChatBotMessage("Thinking...");
    setState((prev) => ({
      ...prev,
      messages: [...prev.messages, botLoading],
    }));

    try {
      // ALWAYS gather context first, regardless of message type
      let currentCode = editorCode;
      let executionResult = "";
      
      console.log("[CHATBOT] Starting context gathering...");
      console.log("[CHATBOT] Current editorCode state:", editorCode ? `${editorCode.length} chars` : "empty");
      console.log("[CHATBOT] Is in EarSketch:", isInEarSketch.current);
      
      // Only try to get context from EarSketch if we're actually embedded
      if (isInEarSketch.current) {
        try {
          // Call sequentially — EarSketch processes one request at a time (no concurrent ids).
          console.log("[CHATBOT] Calling getEditorContents then getScriptExecutionResult...");
          const freshCode = await callEarSketchFunction("getEditorContents");
          const execResult = await callEarSketchFunction("getScriptExecutionResult");
          
          console.log("[CHATBOT] Received freshCode:", typeof freshCode, freshCode ? `${freshCode.length} chars` : "empty/null");
          console.log("[CHATBOT] Received execResult:", typeof execResult, execResult ? `${execResult.length} chars` : "empty/null");
          
          if (typeof freshCode === "string" && freshCode) {
            currentCode = freshCode;
            setEditorCode(freshCode);
            console.log("[CHATBOT] Updated currentCode from freshCode");
          } else {
            console.warn("[CHATBOT] freshCode was empty or invalid, using existing editorCode");
          }
          
          if (typeof execResult === "string" && execResult) {
            executionResult = execResult;
            console.log("[CHATBOT] Set executionResult");
          } else {
            console.warn("[CHATBOT] execResult was empty or invalid");
          }
        } catch (err) {
          console.error("[CHATBOT] Error gathering context:", err);
          // Continue with existing context
        }
      } else {
        console.warn("[CHATBOT] Not embedded in EarSketch - cannot fetch editor contents automatically");
        console.warn("[CHATBOT] Using existing editorCode state:", currentCode ? `${currentCode.length} chars` : "empty");
      }
      
      console.log("[CHATBOT] Final context - code:", currentCode ? `${currentCode.length} chars` : "empty", "execResult:", executionResult ? `${executionResult.length} chars` : "empty");

      let result;
      const lower = decodedMessage.toLowerCase();

      // Handle special commands (for debugging/testing)
      if (lower.trim() === "health") {
        const healthRes = await fetch(`${CHATBOT_API_BASE}/health`);
        const text = await healthRes.text();
        result = healthRes.ok ? `Health OK: ${text}` : `Health check failed: ${text}`;
      } else if (lower.includes("show editor contents")) {
        // Direct command to show editor contents without going to backend
        result = currentCode || "No editor contents available";
      } else if (lower.includes("show run result")) {
        // Direct command to show execution result without going to backend
        result = executionResult || "No execution result available";
      } else if (lower.includes("show daw state")) {
        result = await callEarSketchFunction("getDawState");
      } else if (lower.includes("show color theme")) {
        const theme = await callEarSketchFunction("getColorTheme");
        setIsDarkTheme(theme === "dark");
        result = `Current theme: ${theme}`;
      } else if (lower.includes("markdown")) {
        // Test markdown rendering with a predefined markdown string
        result = markdownTest;
      } else {
        // Send ALL other messages to backend with full context
        const res = await fetch(`${CHATBOT_API_BASE}/api/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            query: decodedMessage,
            code: currentCode,
            executionResult: executionResult,
            userId: userId || "anonymous"
          }),
        });
        result = await res.text();
      }

      const textResult =
        typeof result === "object" ? JSON.stringify(result, null, 2) : result;

      const botReply = {
        type: "markdownOnly",
        id: new Date().getTime(),
        message: textResult,
        payload: textResult,
        loading: false,
      };


      // Replace the loading message with the bot reply
      setState((prev) => ({
        ...prev,
        messages: [...prev.messages.slice(0, -1), botReply],
      }));
    } catch (error) {
      console.error("Error talking to backend:", error);
      const errMsg = createChatBotMessage("Oops, failed to connect to the server.");
      setState((prev) => ({
        ...prev,
        messages: [...prev.messages.slice(0, -1), errMsg],
      }));
    }
  };

  // ----------------- Initialize user session on mount -----------------
  useEffect(() => {
    (async () => {
      try {
        // Get user ID from EarSketch
        const uid = await callEarSketchFunction("getCurrentUser");
        if (uid && typeof uid === "string") {
          console.log("[CHATBOT] User ID:", uid);
          setUserId(uid);
          // Initialize session with backend
          await initializeSession(uid);
        } else {
          console.warn("[CHATBOT] No user ID received, using anonymous");
          setUserId("anonymous");
          await initializeSession("anonymous");
        }
        
        // Get color theme
        const theme = await callEarSketchFunction("getColorTheme");
        setIsDarkTheme(theme === "dark");
        const root = document.documentElement;
        if (theme === "dark") {
          root.classList.add('dark');
          root.setAttribute('data-theme', 'dark');
        } else {
          root.classList.remove('dark');
          root.setAttribute('data-theme', 'light');
        }
      } catch (e) {
        console.warn("Failed to initialize session:", e);
        // Fallback to anonymous
        setUserId("anonymous");
        await initializeSession("anonymous");
      }
    })();
  }, [callEarSketchFunction, initializeSession]);

  // ----------------- Expose actions -----------------
  const actions = {
    handleUserMessage,
    refreshEditor: () => callEarSketchFunction("getEditorContents").then((code) => {
      if (typeof code === "string" && code) setEditorCode(code);
    }),
    callEarSketchFunction,
    prefillChatInput,
    isDarkTheme,
  };

  return (
    <div>
      {React.Children.map(children, (child) =>
        React.cloneElement(child, {
          actions: { handleUserMessage },
        })
      )}
    </div>
  );
};

export default ActionProvider;
